import { Injectable, inject } from '@angular/core';
import { Auth, signOut } from '@angular/fire/auth';
import { AuthService } from 'src/app/auth/services/auth.service';
import { environment } from 'src/environments/environment';
import { SnackbarService } from './snackbar.service';

/**
 * Moves the browser to a backend session for another organization: the API
 * mints the new session (revoking the current one), the custom token and
 * session ID are parked in sessionStorage for AuthService to consume after
 * the reload, and the app reloads on `reloadUrl`.
 *
 * Shared by the side-nav organization switcher and the brand-host
 * registration flow, which must land the new organization's session before
 * the storefront can read that organization's relationship state.
 */
@Injectable({ providedIn: 'root' })
export class OrganizationSwitchService {
  private auth = inject(Auth);
  private authService = inject(AuthService);
  private snackbar = inject(SnackbarService);

  /**
   * Resolves false — after restoring the session monitor and showing the
   * failure snackbar — when the switch did not happen. On success the page
   * reloads and the promise never settles for the caller.
   */
  async switchTo(orgId: string, reloadUrl: string): Promise<boolean> {
    // Captured before the monitor stops so a failed switch can re-arm it.
    let currentSessionId: string | undefined;
    try {
      const tokenResult = await this.auth.currentUser?.getIdTokenResult();
      currentSessionId = tokenResult?.claims?.['sid'] as string | undefined;
    } catch {
      // Unable to read session ID; recovery won't restart the monitor
    }

    try {
      // The switch revokes the current session; the monitor would treat that
      // revocation as a logout.
      this.authService.stopSessionMonitor();

      const idToken = await this.auth.currentUser?.getIdToken();
      if (!idToken) {
        this.restore(currentSessionId);
        return false;
      }

      const apiUrl = environment.apiUrl || '';
      const res = await fetch(`${apiUrl}/api/v1/auth/switch-org`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${idToken}` },
        body: JSON.stringify({ organizationId: orgId, revokeCurrentSession: true }),
      });
      if (!res.ok) {
        throw new Error(`Switch failed: ${res.status}`);
      }
      const data = await res.json();

      if (data.customToken) {
        sessionStorage.setItem('pendingCustomToken', data.customToken);
      }
      if (data.session?.id) {
        sessionStorage.setItem('pendingSessionId', data.session.id);
      }
      sessionStorage.setItem('pendingOrgSwitch', String(Date.now()));

      document.body.classList.add('org-switching');
      await signOut(this.auth);
      localStorage.setItem('orgId', orgId);
      window.location.replace(reloadUrl);
      return true;
    } catch (error) {
      console.error('[OrganizationSwitch] Failed to switch organization:', error);
      document.body.classList.remove('org-switching');
      this.restore(currentSessionId);
      return false;
    }
  }

  private restore(currentSessionId: string | undefined): void {
    if (currentSessionId) this.authService.startSessionMonitor(currentSessionId);
    this.snackbar.tError('errors.orgSwitchFailed');
  }
}
